// Pure model of the adscription modal, ROW BY ROW (no React, relative imports only: the web test runner transpiles
// each file on its own). The modal lists one row per unit and everything the researcher clicks is STAGED here;
// nothing is persisted until submit. This file owns every rule of that staging:
//
//   - what each row may do (move, make principal, remove, restore),
//   - which staged changes apply AT ONCE (the order of the units the researcher ALREADY has and the name of "Otra":
//     they only touch the personal-data mirror rows of those units) and which need an administrator (adding,
//     removing or changing the principal: the shared `diffAdscripcion` items). A new unit can be placed anywhere in the
//     list like any other row, but WHERE it sits is part of the request (its target carries the full staged order of the
//     additional units), never an immediate change,
//   - the button label, the explanation near the footer, the reason why submitting is blocked and the payloads.
//
// A unit has no visibility: the researcher either belongs to it (and it is shown) or does not, like a research line.
//
// The domain core (validation, diff) lives in `@siiv/schemas`; the form-level payload builders in `adscripcionModel`.

import {
	MAX_UNIDADES_ADICIONALES,
	diffAdscripcion,
	esUnidadOtra,
	itemPideMotivo,
	itemsSinMotivo,
	mensajeViolacionAdscripcion,
	motivosDeItems,
	textoCortoItemAdscripcion,
	validarEstadoAdscripcion,
	type CreateSolicitudInput,
	type EstadoAdscripcion,
	type ItemAdscripcion,
} from '@siiv/schemas';
import type { FilaEspejo } from '../../afiliacion/afiliacion';
import {
	construirPayloadDirecto,
	construirPayloadSolicitud,
	descripcionReemplazo,
	type FormularioAdscripcion,
	type ModoAdscripcion,
	type UnidadCatalogoMin,
} from './adscripcionModel';

// ── Saved state ───────────────────────────────────────────

/** A unit the researcher has today, with the mirror row (personal data) that carries its order and name. */
export interface UnidadVigente {
	idUnidad: number;
	/** Key of the mirror row; null when the source does not carry it (the unit then has no immediate controls). */
	fila: FilaEspejo | null;
	/** Displayed text of the mirror row: the custom name when the unit is "Otra". */
	texto: string;
}

/** The saved state the modal starts from: the principal and the additional units in their saved order. */
export interface BaseEdicion {
	principal: UnidadVigente | null;
	adicionales: UnidadVigente[];
}

/** A personal-data row as `GET :slug/datos-personales` returns it (only what the units need). */
export interface FilaEspejoDeUnidad {
	codigo: string;
	valorTexto: string | null;
	ordenVisual: number;
	/** Unit the row mirrors (`valor_json.idUnidad`); null for any other data. */
	idUnidad: number | null;
	idTipoDatoPersonal: number;
	secuencia: number;
}

const CODIGO_UNIDAD = 'unidad';
const CODIGO_UNIDAD_ADICIONAL = 'unidad_adicional';

/**
 * The saved state out of the adscription context (which units are ACTIVE) and the mirror rows (their order
 * and name). Rows are matched by unit id, never by position, and a row is trusted only when it points at the unit it
 * stands for. The additional units come in the saved order of their rows (ties broken like the API does); one with no row
 * goes last with no key, so it simply has no immediate controls. Null for a researcher with no principal.
 */
export function construirBase(
	contexto: { principal: { idUnidad: number } | null; adicionales: { idUnidad: number }[] },
	filas: readonly FilaEspejoDeUnidad[],
): BaseEdicion | null {
	if (!contexto.principal) return null;
	const clave = (fila: FilaEspejoDeUnidad): FilaEspejo => ({ idTipoDatoPersonal: fila.idTipoDatoPersonal, secuencia: fila.secuencia });
	const texto = (fila: FilaEspejoDeUnidad | undefined) => fila?.valorTexto?.trim() ?? '';

	const idPrincipal = contexto.principal.idUnidad;
	const filaPrincipal = filas.find((fila) => fila.codigo === CODIGO_UNIDAD && fila.idUnidad === idPrincipal);

	const activas = new Set(contexto.adicionales.map((unidad) => unidad.idUnidad));
	const conFila = new Map<number, FilaEspejoDeUnidad>();
	for (const fila of filas) {
		if (fila.codigo !== CODIGO_UNIDAD_ADICIONAL || fila.idUnidad === null) continue;
		if (activas.has(fila.idUnidad) && !conFila.has(fila.idUnidad)) conFila.set(fila.idUnidad, fila);
	}
	// Same tie-break the API uses when it lists the rows: saved order, then type, then sequence.
	const ordenadas = [...conFila.values()].sort(
		(a, b) => a.ordenVisual - b.ordenVisual || a.idTipoDatoPersonal - b.idTipoDatoPersonal || a.secuencia - b.secuencia,
	);

	return {
		principal: { idUnidad: idPrincipal, fila: filaPrincipal ? clave(filaPrincipal) : null, texto: texto(filaPrincipal) },
		adicionales: [
			...ordenadas.map((fila) => ({
				idUnidad: fila.idUnidad as number,
				fila: clave(fila),
				texto: texto(fila),
			})),
			...contexto.adicionales
				.filter((unidad) => !conFila.has(unidad.idUnidad))
				.map((unidad) => ({ idUnidad: unidad.idUnidad, fila: null, texto: '' })),
		],
	};
}

// ── Staged state ──────────────────────────────────────────

/** An additional unit as the researcher has it staged. A `quitada` row stays listed (struck through) until submit. */
export interface FilaEdicion {
	idUnidad: number;
	quitada: boolean;
}

export interface EdicionUnidades {
	/** 0 while there is no principal (a researcher without units). */
	idPrincipal: number;
	/** The additional units in the staged order. */
	filas: FilaEdicion[];
	/** Name of "Otra": the current custom name of a saved one (editable) or the name of a new one. */
	nombreOtra: string;
	/**
	 * The reason of each structural change, keyed by item id (`principal`, `agregar-<id>`, `quitar-<id>`) and written in
	 * the row of that change. A reason lives only while its change is staged: undoing the change drops it (`podarMotivos`).
	 */
	motivos: Record<string, string>;
}

const vigenteDe = (base: BaseEdicion | null, idUnidad: number): UnidadVigente | undefined =>
	base?.adicionales.find((unidad) => unidad.idUnidad === idUnidad);

/** A saved additional unit with a mirror row: the only kind whose order can change at once. */
const esEditableAhora = (base: BaseEdicion | null, idUnidad: number): boolean => {
	const vigente = vigenteDe(base, idUnidad);
	return vigente !== undefined && vigente.fila !== null;
};

const unidadesVigentes = (base: BaseEdicion | null): UnidadVigente[] => [
	...(base?.principal ? [base.principal] : []),
	...(base?.adicionales ?? []),
];

const otraVigente = (base: BaseEdicion | null): UnidadVigente | undefined =>
	unidadesVigentes(base).find((unidad) => esUnidadOtra(unidad.idUnidad));

const activas = (edicion: EdicionUnidades): FilaEdicion[] => edicion.filas.filter((fila) => !fila.quitada);

const hayOtraEnObjetivo = (edicion: EdicionUnidades): boolean =>
	esUnidadOtra(edicion.idPrincipal) || activas(edicion).some((fila) => esUnidadOtra(fila.idUnidad));

export function edicionInicial(base: BaseEdicion | null): EdicionUnidades {
	return {
		idPrincipal: base?.principal?.idUnidad ?? 0,
		filas: (base?.adicionales ?? []).map((unidad) => ({ idUnidad: unidad.idUnidad, quitada: false })),
		nombreOtra: otraVigente(base)?.texto.trim() ?? '',
		motivos: {},
	};
}

export function estadoBase(base: BaseEdicion | null): EstadoAdscripcion | null {
	if (!base?.principal) return null;
	return {
		idUnidadPrincipal: base.principal.idUnidad,
		idUnidadesAdicionales: base.adicionales.map((unidad) => unidad.idUnidad),
	};
}

/** The state the edition asks for: the principal plus the additional units not marked for removal. Null without a principal. */
export function objetivoDe(edicion: EdicionUnidades): EstadoAdscripcion | null {
	if (!edicion.idPrincipal) return null;
	return { idUnidadPrincipal: edicion.idPrincipal, idUnidadesAdicionales: activas(edicion).map((fila) => fila.idUnidad) };
}

// ── Editing ───────────────────────────────────────────────

/** Room for one more additional unit (units marked for removal do not count: they are leaving). */
export function puedeAgregarMas(edicion: EdicionUnidades): boolean {
	return activas(edicion).length < MAX_UNIDADES_ADICIONALES;
}

/**
 * Units the "Agregar unidad" select offers: not the principal and not any unit already listed (a removed one comes back
 * with its own "Restaurar", so there is a single way to do it). "Otra" is ONE unit (one id), so excluding what is
 * already listed is also what keeps it from appearing twice.
 */
export function opcionesAgregables(catalogo: UnidadCatalogoMin[], edicion: EdicionUnidades): UnidadCatalogoMin[] {
	const enLista = new Set([edicion.idPrincipal, ...edicion.filas.map((fila) => fila.idUnidad)]);
	return catalogo.filter((unidad) => !enLista.has(unidad.idUnidad));
}

/**
 * Puts a row back where the saved order had it: a unit that is saved as additional (it was promoted and came back, or
 * it is the old principal returning) goes before the first listed saved unit that follows it in the saved order, or right
 * after the last listed saved unit that precedes it (never past the units that are only staged); any other row goes last.
 */
function insertarEnOrdenBase(base: BaseEdicion | null, filas: FilaEdicion[], nueva: FilaEdicion): FilaEdicion[] {
	const posicionGuardada = (idUnidad: number) => base?.adicionales.findIndex((unidad) => unidad.idUnidad === idUnidad) ?? -1;
	const propia = posicionGuardada(nueva.idUnidad);
	if (propia === -1) return [...filas, nueva];
	const destino = filas.findIndex((fila) => posicionGuardada(fila.idUnidad) > propia);
	if (destino !== -1) return [...filas.slice(0, destino), nueva, ...filas.slice(destino)];
	const anterior = filas.findLastIndex((fila) => {
		const posicion = posicionGuardada(fila.idUnidad);
		return posicion !== -1 && posicion < propia;
	});
	return anterior === -1 ? [...filas, nueva] : [...filas.slice(0, anterior + 1), nueva, ...filas.slice(anterior + 1)];
}

/**
 * Adds a unit. `motivo` is the reason the add block already collected for it (the row asks for it when it is empty).
 */
export function agregarUnidad(base: BaseEdicion | null, edicion: EdicionUnidades, idUnidad: number, motivo = ''): EdicionUnidades {
	if (!idUnidad || idUnidad === edicion.idPrincipal) return edicion;
	if (edicion.filas.some((fila) => fila.idUnidad === idUnidad)) return edicion;
	if (!puedeAgregarMas(edicion)) return edicion;
	const fila: FilaEdicion = { idUnidad, quitada: false };
	const conFila = { ...edicion, filas: insertarEnOrdenBase(base, edicion.filas, fila) };
	// A unit that is not listed yet is always a NEW unit (`agregar-<id>`), so its reason needs no check against the staged items.
	return motivo === '' ? conFila : { ...conFila, motivos: { ...conFila.motivos, [`agregar-${idUnidad}`]: motivo } };
}

/**
 * A saved additional unit stays listed, marked, so it can be restored. So does the former principal (it departs unless the
 * researcher keeps it as additional, so the way back is "keep it"). A unit that is only staged (a new one) has nothing to
 * restore and simply leaves the list.
 */
export function quitarUnidad(base: BaseEdicion | null, edicion: EdicionUnidades, idUnidad: number): EdicionUnidades {
	const fila = edicion.filas.find((candidata) => candidata.idUnidad === idUnidad);
	if (!fila) return edicion;
	if (esUnidadNueva(base, idUnidad)) {
		return podarMotivos(base, { ...edicion, filas: edicion.filas.filter((candidata) => candidata.idUnidad !== idUnidad) });
	}
	if (fila.quitada) return edicion;
	return podarMotivos(base, {
		...edicion,
		filas: edicion.filas.map((candidata) => (candidata.idUnidad === idUnidad ? { ...candidata, quitada: true } : candidata)),
	});
}

/** Puts a removed row back: its removal is undone, so the reason typed for it (`quitar-<id>`) is dropped. */
export function restaurarUnidad(edicion: EdicionUnidades, idUnidad: number): EdicionUnidades {
	const fila = edicion.filas.find((candidata) => candidata.idUnidad === idUnidad);
	if (!fila || !fila.quitada || !puedeAgregarMas(edicion)) return edicion;
	const motivos = { ...edicion.motivos };
	delete motivos[`quitar-${idUnidad}`];
	return {
		...edicion,
		filas: edicion.filas.map((candidata) => (candidata.idUnidad === idUnidad ? { ...candidata, quitada: false } : candidata)),
		motivos,
	};
}

export type DireccionDeMovimiento = 'subir' | 'bajar';

/**
 * Rows whose place can change: every row not marked for removal, whether the unit is saved (its order applies at once) or
 * only staged (a new unit, the former principal: its place goes with the request). The one exception is a saved unit with no
 * mirror row, which has no key to order by. Units marked for removal keep their slot and are skipped.
 */
const movibles = (base: BaseEdicion | null, edicion: EdicionUnidades): FilaEdicion[] =>
	edicion.filas.filter((fila) => !fila.quitada && (vigenteDe(base, fila.idUnidad) === undefined || esEditableAhora(base, fila.idUnidad)));

export function movimientosDe(
	base: BaseEdicion | null,
	edicion: EdicionUnidades,
	idUnidad: number,
): { subir: boolean; bajar: boolean } {
	const lista = movibles(base, edicion);
	const indice = lista.findIndex((fila) => fila.idUnidad === idUnidad);
	if (indice === -1) return { subir: false, bajar: false };
	return { subir: indice > 0, bajar: indice < lista.length - 1 };
}

/** Swaps the row with the nearest movable neighbour; units marked for removal keep their slot. */
export function moverUnidad(
	base: BaseEdicion | null,
	edicion: EdicionUnidades,
	idUnidad: number,
	direccion: DireccionDeMovimiento,
): EdicionUnidades {
	const posibles = movimientosDe(base, edicion, idUnidad);
	if (direccion === 'subir' ? !posibles.subir : !posibles.bajar) return edicion;
	const lista = movibles(base, edicion);
	const indice = lista.findIndex((fila) => fila.idUnidad === idUnidad);
	const vecina = lista[direccion === 'subir' ? indice - 1 : indice + 1];
	const desde = edicion.filas.findIndex((fila) => fila.idUnidad === idUnidad);
	const hasta = edicion.filas.findIndex((fila) => fila.idUnidad === vecina.idUnidad);
	const filas = [...edicion.filas];
	[filas[desde], filas[hasta]] = [filas[hasta], filas[desde]];
	return { ...edicion, filas };
}

/**
 * An additional unit becomes the principal. The principal that was SAVED as principal stays listed but marked to leave (that is
 * what changing the principal does unless it is kept: it departs, and the research lines only it backs close), and
 * "Mantener como adicional" (`restaurarUnidad`) brings it back as an additional unit. Any other principal that steps down (a saved
 * additional unit that returns, or a staged one) just goes back to the additional list.
 */
export function hacerPrincipal(base: BaseEdicion | null, edicion: EdicionUnidades, idUnidad: number): EdicionUnidades {
	const fila = edicion.filas.find((candidata) => candidata.idUnidad === idUnidad);
	if (!fila || fila.quitada || idUnidad === edicion.idPrincipal) return edicion;
	const sinPromovida = edicion.filas.filter((candidata) => candidata.idUnidad !== idUnidad);
	const anterior = edicion.idPrincipal;
	const sale = anterior !== 0 && anterior === base?.principal?.idUnidad;
	const filas = anterior
		? insertarEnOrdenBase(base, sinPromovida, { idUnidad: anterior, quitada: sale })
		: sinPromovida;
	// A reason follows the unit across the add/principal switch. The principal that steps down first (if it is new to the researcher,
	// its principal reason goes back to its addition), then the unit that steps up (if it is new, its addition reason becomes the
	// principal one). Nothing is overwritten.
	let motivos = edicion.motivos;
	if (anterior !== 0 && esUnidadNueva(base, anterior)) motivos = trasladarMotivo(motivos, 'principal', `agregar-${anterior}`);
	if (esUnidadNueva(base, idUnidad)) motivos = trasladarMotivo(motivos, `agregar-${idUnidad}`, 'principal');
	return podarMotivos(base, { ...edicion, idPrincipal: idUnidad, filas, motivos });
}

/** The unit is not part of the saved adscription at all (neither the principal nor an additional unit): it is new to the researcher. */
const esUnidadNueva = (base: BaseEdicion | null, idUnidad: number): boolean =>
	vigenteDe(base, idUnidad) === undefined && idUnidad !== base?.principal?.idUnidad;

/** Moves the reason typed under `de` to `a`, but only when `a` has none yet (a blank text counts as none). Same object back otherwise. */
function trasladarMotivo(motivos: Record<string, string>, de: string, a: string): Record<string, string> {
	if ((motivos[de] ?? '').trim() === '' || (motivos[a] ?? '').trim() !== '') return motivos;
	const resultado = { ...motivos, [a]: motivos[de] };
	delete resultado[de];
	return resultado;
}

/** Chooses the principal of a researcher who has none (an administrator assigning the first unit). */
export function establecerPrincipal(edicion: EdicionUnidades, idUnidad: number): EdicionUnidades {
	if (edicion.idPrincipal !== 0 || !idUnidad) return edicion;
	return { ...edicion, idPrincipal: idUnidad, filas: edicion.filas.filter((fila) => fila.idUnidad !== idUnidad) };
}

// ── Classifying the staged changes ────────────────────────

export interface CambiosEdicion {
	/** What an administrator must approve: the items of the shared diff (principal, conservar, agregar, quitar). */
	estructurales: ItemAdscripcion[];
	/**
	 * Immediate: the mirror rows of the saved additional units in their new order, when the order of the units that stay
	 * changed (a unit being removed keeps its slot so what is saved stays a full list). Null when it did not change.
	 */
	orden: FilaEspejo[] | null;
	/** Immediate: a saved "Otra" got a new name (it only touches its mirror row). */
	renombre: { idUnidad: number; fila: FilaEspejo; nombre: string } | null;
	/**
	 * How many units that are NOT saved as additional yet (new ones, the former principal kept) are staged among other
	 * additional units: the position of each one is part of the request. 0 when there is nothing to place, or the only
	 * additional unit has no position to choose.
	 */
	nuevasConPosicion: number;
}

function itemsEstructurales(base: BaseEdicion | null, objetivo: EstadoAdscripcion | null): ItemAdscripcion[] {
	if (!objetivo) return [];
	const actual = estadoBase(base);
	if (actual) return diffAdscripcion(actual, objetivo);
	// No unit today: the first principal and every additional unit are new.
	return [
		{ id: 'principal', tipo: 'cambiar_principal', idUnidad: objetivo.idUnidadPrincipal, desde: null },
		...objetivo.idUnidadesAdicionales.map(
			(idUnidad): ItemAdscripcion => ({ id: `agregar-${idUnidad}`, tipo: 'agregar_adicional', idUnidad }),
		),
	];
}

export function clasificarCambios(base: BaseEdicion | null, edicion: EdicionUnidades): CambiosEdicion {
	const objetivo = objetivoDe(edicion);

	// Saved additional units, in the staged order (removed ones included, they keep a slot; new ones are left out: where
	// they sit goes with the request). The order only counts as changed when the units that stay end up in a different
	// relative order than the saved one.
	const guardadas = edicion.filas.filter((fila) => esEditableAhora(base, fila.idUnidad));
	const quedan = guardadas.filter((fila) => !fila.quitada).map((fila) => fila.idUnidad);
	const ordenGuardado = (base?.adicionales ?? [])
		.filter((unidad) => unidad.fila !== null && quedan.includes(unidad.idUnidad))
		.map((unidad) => unidad.idUnidad);
	const ordenCambia = quedan.some((idUnidad, indice) => idUnidad !== ordenGuardado[indice]);
	const orden = ordenCambia ? guardadas.map((fila) => vigenteDe(base, fila.idUnidad)?.fila as FilaEspejo) : null;

	const otra = otraVigente(base);
	const nombre = edicion.nombreOtra.trim();
	const otraSigue = otra !== undefined && hayOtraEnObjetivo(edicion);
	const renombre =
		otra?.fila && otraSigue && nombre && nombre !== otra.texto.trim()
			? { idUnidad: otra.idUnidad, fila: otra.fila, nombre }
			: null;

	const enLista = activas(edicion);
	const nuevasConPosicion =
		enLista.length >= 2 ? enLista.filter((fila) => vigenteDe(base, fila.idUnidad) === undefined).length : 0;

	return { estructurales: itemsEstructurales(base, objetivo), orden, renombre, nuevasConPosicion };
}

export const hayInmediatos = (cambios: CambiosEdicion): boolean => cambios.orden !== null || cambios.renombre !== null;

export const hayEstructurales = (cambios: CambiosEdicion): boolean => cambios.estructurales.length > 0;

export const hayCambios = (cambios: CambiosEdicion): boolean => hayInmediatos(cambios) || hayEstructurales(cambios);

// ── One reason per change ─────────────────────────────────

/**
 * The structural change each ROW asks a reason for (request mode only; an administrator never writes one): unit id of the row
 * -> the item it carries. The new unit asks in its own row (`agregar-<id>`), the new principal in its row (`principal`) and every
 * removed unit in its row (`quitar-<id>`). The derived `conservar` asks nothing, and neither does the former principal that
 * leaves (its departure is part of the principal change). A field never moves: the row of a change is fixed by the change.
 */
export function itemsPorFila(cambios: CambiosEdicion, modo: ModoAdscripcion): ReadonlyMap<number, ItemAdscripcion> {
	const filas = new Map<number, ItemAdscripcion>();
	if (modo === 'directo') return filas;
	for (const item of cambios.estructurales) if (itemPideMotivo(item)) filas.set(item.idUnidad, item);
	return filas;
}

/** Drops the reasons whose change is not staged any more (the change was undone). Same object back when nothing is dropped. */
export function podarMotivos(base: BaseEdicion | null, edicion: EdicionUnidades): EdicionUnidades {
	const vigentes = new Set(itemsEstructurales(base, objetivoDe(edicion)).filter(itemPideMotivo).map((item) => item.id as string));
	const claves = Object.keys(edicion.motivos);
	if (claves.every((clave) => vigentes.has(clave))) return edicion;
	return { ...edicion, motivos: Object.fromEntries(claves.filter((clave) => vigentes.has(clave)).map((clave) => [clave, edicion.motivos[clave]])) };
}

/** Writes the reason of a staged change; a change that is not staged (or that takes no reason) leaves the edition untouched. */
export function fijarMotivo(base: BaseEdicion | null, edicion: EdicionUnidades, idItem: string, texto: string): EdicionUnidades {
	const existe = itemsEstructurales(base, objetivoDe(edicion)).some((item) => item.id === idItem && itemPideMotivo(item));
	return existe ? { ...edicion, motivos: { ...edicion.motivos, [idItem]: texto } } : edicion;
}

// ── Button, explanation and reason ────────────────────────

/**
 * The submit button: "Guardar cambios" while only the order or the name of "Otra" changed (they apply at once) or for an
 * administrator (everything applies at once); "Enviar solicitud" as soon as anything structural is staged.
 */
export function textoBotonGuardar(cambios: CambiosEdicion, modo: ModoAdscripcion, enCurso = false): string {
	const esSolicitud = modo === 'solicitud' && hayEstructurales(cambios);
	if (enCurso) return esSolicitud ? 'Enviando…' : 'Guardando…';
	return esSolicitud ? 'Enviar solicitud' : 'Guardar cambios';
}

/**
 * One piece of the explanation: a heading line followed by one bullet per change (none for a plain sentence). `inmediato`
 * and `aprobacion` are what the staged changes do; `info` is the general rule (shown while there is nothing staged to say);
 * `nota` is the remark about the position of the new units; `lineas` names the research lines that would close.
 */
export interface BloqueExplicacion {
	tipo: 'inmediato' | 'aprobacion' | 'info' | 'lineas' | 'nota';
	encabezado: string;
	puntos: string[];
}

export interface ExplicacionCambios {
	/** One bullet per change that applies at once (for an administrator: every change). */
	inmediatos: string[];
	/** One bullet per change that needs an administrator (request mode only). */
	aprobacion: string[];
	/** What the tooltip shows, in order. */
	bloques: BloqueExplicacion[];
}

const ENCABEZADO_INMEDIATO = 'Se aplica de inmediato:';
const ENCABEZADO_APROBACION = 'Requiere aprobación de un administrador:';
const ENCABEZADO_INMEDIATO_ADMIN = 'Como administrador, se aplica de inmediato:';

/** The general rule as two blocks: what applies at once and what an administrator reviews. */
const REGLA_INMEDIATA: BloqueExplicacion = {
	tipo: 'info',
	encabezado: ENCABEZADO_INMEDIATO,
	puntos: ['El orden de las unidades adicionales', 'El nombre de la unidad «Otra»'],
};
const REGLA_REVISION: BloqueExplicacion = {
	tipo: 'info',
	encabezado: ENCABEZADO_APROBACION,
	puntos: ['Agregar o quitar unidades', 'Cambiar la unidad principal'],
};

const textoDeItem = textoCortoItemAdscripcion;

/**
 * The block naming the research lines the change would close, or none when no line closes. A request only
 * "would" close them (an administrator decides); an administrator's direct change does.
 */
function bloqueDeLineasCerradas(lineasCerradas: readonly string[], modo: ModoAdscripcion): BloqueExplicacion[] {
	const nombres = lineasCerradas.map((nombre) => nombre.trim()).filter((nombre) => nombre !== '');
	if (nombres.length === 0) return [];
	const encabezado = modo === 'directo' ? 'Se cerrarán estas líneas:' : 'Se cerrarían estas líneas:';
	return [{ tipo: 'lineas', encabezado, puntos: nombres }];
}

/** The remark that the position of a unit being added goes with the request (singular or plural); none when there is none to place. */
function notaDePosicion(cambios: CambiosEdicion): BloqueExplicacion[] {
	if (cambios.nuevasConPosicion === 0) return [];
	const encabezado =
		cambios.nuevasConPosicion === 1
			? 'La posición de la unidad que se agrega se define con la solicitud.'
			: 'La posición de las unidades que se agregan se define con la solicitud.';
	return [{ tipo: 'nota', encabezado, puntos: [] }];
}

/**
 * The Spanish copy that splits the staged changes into "se aplica de inmediato" and "requiere aprobación": a heading per
 * group and one capitalised bullet per change (no chaining with commas), plus the remark about where a new unit sits and
 * the research lines that would close (already named by the caller; none by default).
 */
export function explicarCambios(
	cambios: CambiosEdicion,
	modo: ModoAdscripcion,
	nombres: ReadonlyMap<number, string>,
	lineasCerradas: readonly string[] = [],
): ExplicacionCambios {
	const nombreDe = (idUnidad: number) => nombres.get(idUnidad)?.trim() || `Unidad ${idUnidad}`;
	const cierres = bloqueDeLineasCerradas(lineasCerradas, modo);

	const propios: string[] = [
		...(cambios.orden ? ['Reordenar las unidades adicionales'] : []),
		...(cambios.renombre ? ['Cambiar el nombre de la unidad «Otra»'] : []),
	];
	const estructurales = cambios.estructurales.map((item) => textoDeItem(item, nombreDe));

	if (modo === 'directo') {
		const todos = [...propios, ...estructurales];
		return {
			inmediatos: todos,
			aprobacion: [],
			bloques: [
				todos.length === 0
					? { tipo: 'info', encabezado: 'Como administrador, los cambios se aplican de inmediato y quedan registrados en la auditoría.', puntos: [] }
					: { tipo: 'inmediato', encabezado: ENCABEZADO_INMEDIATO_ADMIN, puntos: todos },
				...cierres,
			],
		};
	}

	const bloques: BloqueExplicacion[] = [];
	if (propios.length > 0) bloques.push({ tipo: 'inmediato', encabezado: ENCABEZADO_INMEDIATO, puntos: propios });
	if (estructurales.length > 0) {
		bloques.push({ tipo: 'aprobacion', encabezado: ENCABEZADO_APROBACION, puntos: estructurales }, ...notaDePosicion(cambios));
	}
	if (bloques.length === 0) bloques.push(REGLA_INMEDIATA, REGLA_REVISION);
	bloques.push(...cierres);
	return { inmediatos: propios, aprobacion: estructurales, bloques };
}

// ── Header pill ───────────────────────────────────────────

export interface PildoraAdscripcion {
	/**
	 * `revision`: an administrator will review part of the edition (orange); `inmediato`: all of it applies at once (neutral);
	 * `pendiente`: the researcher already has a request waiting for review (the plain info look).
	 */
	tono: 'revision' | 'inmediato' | 'pendiente';
	titulo: string;
	/** The tooltip: a heading and a bullet list per group. */
	bloques: BloqueExplicacion[];
}

export const TITULO_PILDORA_REVISION = 'Lo revisará un administrador';
export const TITULO_PILDORA_INMEDIATO = 'Se aplica de inmediato';

/**
 * The pill next to the title of the modal and the tooltip it opens. Orange ("Lo revisará un administrador") as soon as a
 * researcher stages anything that needs approval; neutral ("Se aplica de inmediato") when everything staged applies at
 * once, which is always the case for an administrator. With nothing staged the neutral pill is still shown (with the
 * general rule), so the header never changes height when the first change is staged. The tooltip says what
 * applies at once (the staged part, or the general rule when none of it is staged), what needs approval (and that the
 * position of a new unit goes with the request) and, last, the research lines that would close.
 */
export function pildoraDeAdscripcion(
	cambios: CambiosEdicion,
	modo: ModoAdscripcion,
	nombres: ReadonlyMap<number, string>,
	lineasCerradas: readonly string[] = [],
): PildoraAdscripcion {
	const { bloques } = explicarCambios(cambios, modo, nombres, lineasCerradas);

	if (modo === 'solicitud' && hayEstructurales(cambios)) {
		const inmediatos = bloques.filter((bloque) => bloque.tipo === 'inmediato');
		return {
			tono: 'revision',
			titulo: TITULO_PILDORA_REVISION,
			bloques: [
				...(inmediatos.length > 0 ? inmediatos : [REGLA_INMEDIATA]),
				...bloques.filter((bloque) => bloque.tipo !== 'inmediato'),
			],
		};
	}
	// Nothing staged yet: the pill is still there (the header keeps its height, no jump on the first change) and
	// explains the rule up front.
	if (modo === 'solicitud' && !hayInmediatos(cambios)) {
		return pildoraInicialDeAdscripcion(modo);
	}
	return { tono: 'inmediato', titulo: TITULO_PILDORA_INMEDIATO, bloques };
}

/** The pill before anything is staged (or while the modal loads): neutral, with the general rule. */
export function pildoraInicialDeAdscripcion(modo: ModoAdscripcion): PildoraAdscripcion {
	return {
		tono: 'inmediato',
		titulo: TITULO_PILDORA_INMEDIATO,
		bloques:
			modo === 'solicitud'
				? [REGLA_INMEDIATA, REGLA_REVISION]
				: [{ tipo: 'info', encabezado: 'Como administrador, los cambios se aplican de inmediato.', puntos: [] }],
	};
}

/** "Otra" is new in the target: it needs a name that travels with the request (a saved one is renamed at once). */
export function otraEsNueva(base: BaseEdicion | null, edicion: EdicionUnidades): boolean {
	return hayOtraEnObjetivo(edicion) && otraVigente(base) === undefined;
}

interface EntradaRazon {
	base: BaseEdicion | null;
	edicion: EdicionUnidades;
	modo: ModoAdscripcion;
	/** `id -> name` of the units, to name the rows that lack a reason (without it they read "Unidad N"). */
	nombres?: ReadonlyMap<number, string>;
}

/**
 * The visible reason why submitting is blocked, or null when it can go. Priority: no unit today (request mode), no
 * principal, rules the target breaks, nothing changed, missing name of a new "Otra", missing reasons (a request needs one
 * per structural change; the message names the changes that lack it).
 */
export function razonNoEnviable({ base, edicion, modo, nombres }: EntradaRazon): string | null {
	if (modo === 'solicitud' && !base?.principal) {
		return 'No tienes una unidad vigente, por lo que no puedes solicitar un cambio.';
	}
	const objetivo = objetivoDe(edicion);
	if (!objetivo) return 'Selecciona la unidad principal.';

	const violaciones = validarEstadoAdscripcion(objetivo);
	if (violaciones.length > 0) {
		return violaciones.map((violacion) => mensajeViolacionAdscripcion(violacion)).join(' ');
	}

	const cambios = clasificarCambios(base, edicion);
	if (!hayCambios(cambios)) {
		return modo === 'solicitud'
			? 'No hay cambios respecto de tu adscripción actual.'
			: 'No hay cambios respecto de la adscripción actual.';
	}
	if (otraEsNueva(base, edicion) && edicion.nombreOtra.trim().length === 0) {
		return 'Escribe el nombre de la unidad "Otra".';
	}
	if (modo === 'solicitud') {
		const sinMotivo = itemsSinMotivo(cambios.estructurales, edicion.motivos);
		if (sinMotivo.length > 0) {
			const nombreDe = (idUnidad: number) => nombres?.get(idUnidad)?.trim() || `Unidad ${idUnidad}`;
			return `Falta el motivo de: ${sinMotivo.map((item) => textoDeItem(item, nombreDe)).join(', ')}.`;
		}
	}
	return null;
}

// ── What gets sent ────────────────────────────────────────

export interface PlanDeEnvio {
	cambios: CambiosEdicion;
	/** Request mode with a structural change: the body of `POST /solicitudes`. */
	solicitud: CreateSolicitudInput | null;
	/** Direct mode with a structural change: the body of `PUT :slug/adscripcion`. */
	directo: (EstadoAdscripcion & { nombreOtra: string | null }) | null;
}

/**
 * What submitting does: the immediate part (always through the personal-data endpoints) and, only when something
 * structural is staged, the request (a researcher) or the direct PUT (an administrator). The name of "Otra" travels
 * only when "Otra" is new in the target; a saved one is renamed at once and the server keeps the stored name.
 */
export function construirPlan(base: BaseEdicion | null, edicion: EdicionUnidades, modo: ModoAdscripcion): PlanDeEnvio {
	const cambios = clasificarCambios(base, edicion);
	const objetivo = objetivoDe(edicion);
	if (!objetivo || !hayEstructurales(cambios)) return { cambios, solicitud: null, directo: null };

	const form: FormularioAdscripcion = {
		nombreOtra: otraEsNueva(base, edicion) ? edicion.nombreOtra : '',
		motivos: motivosDeItems(cambios.estructurales, edicion.motivos),
	};
	return modo === 'directo'
		? { cambios, solicitud: null, directo: construirPayloadDirecto(objetivo, form) }
		: { cambios, solicitud: construirPayloadSolicitud(objetivo, form), directo: null };
}

/** Toast description after a request was created: the replaced requests, and that the rest already applied. */
export function descripcionDeSolicitudEnviada(idsReemplazadas: number[], huboInmediatos: boolean): string {
	const base = descripcionReemplazo(idsReemplazadas) ?? 'Un administrador la revisará pronto.';
	return huboInmediatos ? `${base} Los cambios que no requieren aprobación ya se aplicaron.` : base;
}

export const TITULO_PILDORA_PENDIENTE = 'Solicitud pendiente';

/**
 * The second pill of the header, shown ONLY while the researcher has a request waiting for review (null otherwise). Its tooltip
 * says what that request asks for (the items it still changes against the current state, worded like the rest of the
 * modal) and what happens to it now: an edition with something structural replaces it; order and the name of "Otra" apply
 * apart and never touch it. It never names the request by number: that is bookkeeping, not something to read.
 */
export function pildoraSolicitudPendiente(
	pendiente: EstadoAdscripcion | null | undefined,
	actual: EstadoAdscripcion | null,
	hayEstructural: boolean,
	nombres: ReadonlyMap<number, string>,
): PildoraAdscripcion | null {
	if (!pendiente) return null;
	const nombreDe = (idUnidad: number) => nombres.get(idUnidad)?.trim() || `Unidad ${idUnidad}`;
	const pide = actual ? diffAdscripcion(actual, pendiente).map((item) => textoDeItem(item, nombreDe)) : [];
	const bloques: BloqueExplicacion[] = [
		{ tipo: 'info', encabezado: 'Tienes una solicitud de cambio de unidad esperando la revisión de un administrador.', puntos: [] },
		...(pide.length > 0 ? [{ tipo: 'info' as const, encabezado: 'Pide:', puntos: pide }] : []),
		{
			tipo: 'info',
			encabezado: hayEstructural
				? 'Si envías estos cambios, esta solicitud se reemplaza por la nueva.'
				: 'Guardar solo el orden o el nombre de «Otra» no la afecta.',
			puntos: [],
		},
	];
	return { tono: 'pendiente', titulo: TITULO_PILDORA_PENDIENTE, bloques };
}

// ── What the rows show ────────────────────────────────────

export interface FilaVista {
	idUnidad: number;
	principal: boolean;
	/** 1-based place among the additional units shown; 0 for the principal. */
	posicion: number;
	quitada: boolean;
	/** The unit is an additional unit today: its order applies at once. */
	vigente: boolean;
	/** "Otra": the row offers its name. */
	esOtra: boolean;
	/** The unit is not part of the saved adscription at all: it is new to the researcher (a new additional or a new principal). */
	nuevaUnidad: boolean;
	/** `nueva_principal`: a different unit than today's principal; `nueva`: to be added; `era_principal`: the old principal, now additional. */
	marca: 'nueva_principal' | 'nueva' | 'era_principal' | null;
	puedeSubir: boolean;
	puedeBajar: boolean;
	puedeHacerPrincipal: boolean;
	puedeQuitar: boolean;
	puedeRestaurar: boolean;
}

/** Rows in display order: the principal, then the additional units in the staged order (new ones sit where they were placed). */
export function filasParaMostrar(base: BaseEdicion | null, edicion: EdicionUnidades): FilaVista[] {
	const filas: FilaVista[] = [];
	if (edicion.idPrincipal) {
		filas.push({
			idUnidad: edicion.idPrincipal,
			principal: true,
			posicion: 0,
			quitada: false,
			vigente: false,
			nuevaUnidad: esUnidadNueva(base, edicion.idPrincipal),
			esOtra: esUnidadOtra(edicion.idPrincipal),
			marca: base?.principal && base.principal.idUnidad === edicion.idPrincipal ? null : 'nueva_principal',
			puedeSubir: false,
			puedeBajar: false,
			puedeHacerPrincipal: false,
			puedeQuitar: false,
			puedeRestaurar: false,
		});
	}

	edicion.filas.forEach((fila, indice) => {
		const vigente = vigenteDe(base, fila.idUnidad) !== undefined;
		const movimientos = movimientosDe(base, edicion, fila.idUnidad);
		filas.push({
			idUnidad: fila.idUnidad,
			principal: false,
			posicion: indice + 1,
			quitada: fila.quitada,
			vigente,
			nuevaUnidad: esUnidadNueva(base, fila.idUnidad),
			esOtra: esUnidadOtra(fila.idUnidad),
			marca: vigente ? null : fila.idUnidad === base?.principal?.idUnidad ? 'era_principal' : 'nueva',
			puedeSubir: movimientos.subir,
			puedeBajar: movimientos.bajar,
			puedeHacerPrincipal: !fila.quitada,
			puedeQuitar: !fila.quitada,
			puedeRestaurar: fila.quitada,
		});
	});
	return filas;
}
